import {
  ImageType,
  PointOfInterestTemplateField,
  UnitsEnum,
  UnitSystemEnum,
  Maybe,
  PointOfInterest,
} from '@/__generated__/graphql';
import { TemplateDataHookKeys } from './hooks/useTemplateFields';
import { localiseMeasurement } from '@/utils/unitSystem';
import { NOT_APPLICABLE_LABEL, UNCERTAIN_PROFILE_LABEL } from '@/constants';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { ImageWithId } from '@/types';

export const convertTemplateStateToTemplateFieldData = (
  data: TemplateDataHookKeys
): Record<string, string> => {
  const result: Record<string, string> = {};
  Object.entries(data).forEach(([key, value]) => {
    result[key] = value?.toString() ?? '';
  });
  return result;
};

export const convertTemplateDataIntoFieldsArray = ({
  selectedPoi,
  unitSystem,
  templateFields,
  isVerified = false,
  blisterFieldConfig,
}: {
  selectedPoi: Partial<PointOfInterest> | undefined;
  unitSystem: UnitSystemEnum;
  templateFields: PointOfInterestTemplateField[];
  isVerified?: boolean;
  blisterFieldConfig?: {
    maxHeight?: { name: string; hidden: boolean };
    estimatedHeight?: { name: string; hidden: boolean };
    pdrHeight?: { name: string; hidden: boolean };
    wallLoss?: { name: string; hidden: boolean };
    residualCorrosionAllowance?: { name: string; hidden: boolean };
    residualThickness?: { name: string; hidden: boolean };
  };
}): Array<{
  label: string;
  value: string;
}> => {
  // If we're showing verified fields, filter the template fields to only include those with verified values
  const filteredTemplateFields = isVerified
    ? templateFields?.filter((field) => {
        // Only include fields that have a corresponding verified_ key with a value
        const verifiedKey = field.key ? `verified_${field.key}` : '';
        const hasVerifiedValue = verifiedKey && selectedPoi?.templateFieldData;
        return hasVerifiedValue && !field.isHidden && field.name !== 'Comments';
      })
    : templateFields;

  const values = filteredTemplateFields?.map((field) => {
    // For verified fields, get the value from verified_key
    const value =
      isVerified && field.key
        ? (selectedPoi?.templateFieldData?.[`verified_${field.key}`] ?? 'N/A')
        : (selectedPoi?.templateFieldData?.[field.key ?? ''] ?? 'N/A');

    return {
      value: value?.toString?.() ?? 'N/A',
      label: field?.name || 'N/A',
    };
  });

  if (selectedPoi?.blister) {
    const {
      maxHeight,
      estimatedHeight,
      pdrHeight,
      wallLoss,
      residualCorrosionAllowance,
      residualThickness,
    } = selectedPoi.blister;

    const blisterFields = [
      // Max Height field
      ...(!blisterFieldConfig?.maxHeight?.hidden
        ? [
            {
              value:
                localiseMeasurement({
                  unitSystem,
                  value: maxHeight || undefined,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                  showUnitLabel: true,
                  fallbackLabel: UNCERTAIN_PROFILE_LABEL,
                }) || UNCERTAIN_PROFILE_LABEL,
              label: blisterFieldConfig?.maxHeight?.name || 'Blister Height',
            },
          ]
        : []),
      // Estimated Height field
      ...(!blisterFieldConfig?.estimatedHeight?.hidden
        ? [
            {
              value:
                localiseMeasurement({
                  unitSystem,
                  value: estimatedHeight || undefined,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                  showUnitLabel: true,
                }) || UNCERTAIN_PROFILE_LABEL,
              label: blisterFieldConfig?.estimatedHeight?.name || 'Estimated Blister Height',
            },
          ]
        : []),
      // PDR Height field
      ...(!blisterFieldConfig?.pdrHeight?.hidden
        ? [
            {
              value:
                localiseMeasurement({
                  unitSystem,
                  value: pdrHeight || undefined,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                  showUnitLabel: true,
                }) || UNCERTAIN_PROFILE_LABEL,
              label: blisterFieldConfig?.pdrHeight?.name || 'PDR Height',
            },
          ]
        : []),
      // Wall Loss field
      ...(!blisterFieldConfig?.wallLoss?.hidden
        ? [
            {
              value:
                localiseMeasurement({
                  unitSystem,
                  value: wallLoss?.value,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                  showUnitLabel: true,
                }) || NOT_APPLICABLE_LABEL,
              label: blisterFieldConfig?.wallLoss?.name || 'Estimated Wall Loss',
            },
          ]
        : []),
      // Residual Corrosion Allowance field
      ...(!blisterFieldConfig?.residualCorrosionAllowance?.hidden
        ? [
            {
              value:
                localiseMeasurement({
                  unitSystem,
                  value: residualCorrosionAllowance?.value,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                  showUnitLabel: true,
                }) || NOT_APPLICABLE_LABEL,
              label:
                blisterFieldConfig?.residualCorrosionAllowance?.name ||
                'Residual Corrosion Allowance',
            },
          ]
        : []),
      // Residual Thickness field
      ...(!blisterFieldConfig?.residualThickness?.hidden
        ? [
            {
              value:
                localiseMeasurement({
                  unitSystem,
                  value: residualThickness?.value,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                  showUnitLabel: true,
                }) || NOT_APPLICABLE_LABEL,
              label: blisterFieldConfig?.residualThickness?.name || 'Residual Thickness',
            },
          ]
        : []),
    ];
    const finalResultWithBlister = [...blisterFields, ...values];
    return finalResultWithBlister;
  }
  return values;
};
export const generatePoiDisplayName = ({
  name,
  poiId,
  poiType,
}: {
  name: Maybe<string> | undefined;
  poiId: Maybe<string> | undefined;
  poiType: string | undefined;
}) => {
  if (!poiId) return name || poiType || 'N/A';

  if (!name && !poiType) return `POI - ${poiId}`;

  return `${name || poiType} - ${poiId}`;
};

export const mapAttachmentType = (currentImage: ImageWithId | undefined): Attachment | null => {
  if (!currentImage) return null;

  return {
    caption: currentImage.caption ?? undefined,
    id: currentImage.id ?? undefined,
    isNew: false,
    imageType: ImageType.Other,
    name: currentImage.name ?? '',
    resourcePath: currentImage.src,
    uuid: currentImage.uuid ?? undefined,
    type: currentImage.viewpointId ? 'viewpoint' : ImageType.Other,
    fileName: currentImage.fileName,
  };
};
